import { all, call, getContext, put, select, takeLeading } from 'redux-saga/effects';

import type { PasswordChangeDraft } from '../domain/password-change.model';
import { validateDraft } from '../domain/password-change.validation';
import { toPasswordChangeError } from '../domain/password-change-error';
import type { PasswordChangeGateway } from '../infrastructure/password-change.gateway';
import { submitRequested } from './password-change.actions';
import { selectDraft } from './password-change.selectors';
import { passwordChangeActions } from './password-change.slice';

/**
 * WORKER: change the signed-in user's password.
 *
 * Validates first (pure domain, length only) and stops without touching the
 * gateway; that spares a round-trip for the one rule this side can decide. The
 * other refusals (wrong current password, breached password, must differ) come
 * back from the server and are placed on the right field by
 * `toPasswordChangeError`.
 *
 * `takeLeading`, not `takeLatest`. Both look right and only one is: `takeLatest`
 * cancels the previous SAGA TASK, not the request it already sent; a double
 * click would still fire two POSTs, and the second would land after the password
 * had changed, so the user would be told their current password is incorrect
 * about the password they had just chosen. `takeLeading` ignores the second
 * click until the first finishes, which is what "submit once" actually means.
 */
function* submitWorker() {
  const draft = (yield select(selectDraft)) as PasswordChangeDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(passwordChangeActions.submitFailed({ kind: 'validation', fields: errors }));
    return;
  }

  const gateway: PasswordChangeGateway = yield getContext('passwordChangeGateway');
  yield put(passwordChangeActions.submitStarted());
  try {
    const { revokedSessions } = (yield call(gateway.changePassword, draft)) as {
      revokedSessions: number;
    };
    yield put(passwordChangeActions.submitSucceeded({ revokedSessions }));
  } catch (e) {
    yield put(passwordChangeActions.submitFailed(toPasswordChangeError(e)));
  }
}

/** WATCHERS. */
export function* passwordChangeSaga() {
  yield all([takeLeading(submitRequested.type, submitWorker)]);
}
